/**
 * Auth resource — backend-side user sync + password-reset request.
 * Firebase sign-in/sign-out lives in @discovr/common/auth, and the
 * reset-completion flow (verify/confirm oobCode) stays on the Firebase
 * client SDK in ResetPasswordPage.
 *
 * V3 note: signup, reset-completion, and invite-preview flows are managed by
 * Firebase and deferred to v3. Forgot-password requests, however, go through
 * the backend so the reset email is sent via Resend (reliable delivery to
 * corporate domains) instead of Firebase's default sender.
 */
import { apiClient } from '@discovr/common';

export interface LoginToBackendBody {
  // The app the user is logging into, not the user's stored role.
  role: 'admin' | 'brand_owner' | 'creator';
  name?: string;
}

export const auth = {
  /**
   * Sync Firebase user into MongoDB after first sign-in.
   * BE: WriteData(w, 200, { user, message }). Callers only need the side-effect.
   */
  loginToBackend: (body: LoginToBackendBody): Promise<void> =>
    apiClient.post('/auth/login', body).then(() => undefined),

  /**
   * Sync email verification status to MongoDB after Firebase confirms.
   * BE: WriteNoContent → 204.
   */
  syncEmailVerified: (): Promise<void> =>
    apiClient.post('/auth/verify-email').then(() => undefined),

  /**
   * Request a password-reset email. The backend generates the Firebase reset
   * link and sends it via Resend, rewriting the link to the brand/admin app's
   * /auth/action route. BE always returns 200 for brand (anti-enumeration). For
   * admin accounts it sends nothing and returns { admin_managed: true } so the
   * UI can tell the user to ask their super admin.
   *
   * `app` is the calling surface. Pass 'admin' from the admin console: the
   * backend then returns { account_exists: false } for an unknown email so the
   * UI can show "this account doesn't exist". Brand omits it and stays
   * anti-enumeration (account_exists is never returned, so accountExists=true).
   */
  requestPasswordReset: (
    email: string,
    app?: 'admin' | 'brand',
  ): Promise<{ adminManaged: boolean; accountExists: boolean }> =>
    apiClient
      .post('/auth/request-password-reset', { email, ...(app ? { app } : {}) })
      .then((r) => {
        const data = r.data?.data as
          | { admin_managed?: boolean; account_exists?: boolean }
          | undefined;
        return {
          adminManaged: Boolean(data?.admin_managed),
          // BE only sends account_exists=false (admin not-found). Absent → exists.
          accountExists: data?.account_exists !== false,
        };
      }),

  /**
   * Backend-driven Google account link: idToken comes from a Google sign-in
   * performed on a throwaway, non-primary Firebase App instance — never the
   * FE's own signed-in session (see AccountSection.tsx's handleLinkGoogle).
   * The backend independently verifies the token, checks the email matches
   * the caller's account, and attaches the provider via the Admin SDK. The
   * FE never mutates Firebase Auth state directly for this. Throws on
   * mismatch (LINK_EMAIL_MISMATCH) — no Firebase state is touched in that case.
   */
  linkGoogle: (idToken: string): Promise<void> =>
    apiClient.post('/auth/link-google', { id_token: idToken }).then(() => undefined),

  /**
   * Backend-driven Google account unlink: clears the backend's own
   * link-consent record, removes the provider via the Admin SDK, and revokes
   * existing sessions — the FE never calls Firebase's client-side unlink()
   * directly. Throws UNLINK_ONLY_PROVIDER if Google is the account's only
   * sign-in method.
   */
  unlinkGoogle: (): Promise<void> =>
    apiClient.post('/auth/unlink-google').then(() => undefined),

  /**
   * Requests an email sign-in code + link for an existing account. Always
   * returns 200 regardless of whether the email matches an eligible account
   * (enumeration-safe) — the caller should show a generic "check your email"
   * state unconditionally, never branch on the response.
   */
  requestOtp: (email: string, app: 'admin' | 'brand_owner'): Promise<void> =>
    apiClient.post('/auth/otp/request', { email, app }).then(() => undefined),

  /**
   * Verifies a code or link_token (exactly one required) and returns a
   * Firebase custom token — pass it to signInWithOtpToken.
   */
  verifyOtp: (params: {
    email: string;
    app: 'admin' | 'brand_owner';
    code?: string;
    linkToken?: string;
  }): Promise<{ firebaseCustomToken: string }> =>
    apiClient
      .post('/auth/otp/verify', {
        email: params.email,
        app: params.app,
        code: params.code,
        link_token: params.linkToken,
      })
      .then((r) => {
        const data = r.data?.data as { firebase_custom_token: string };
        return { firebaseCustomToken: data.firebase_custom_token };
      }),
};
